// พื้นที่ทดสอบ API สัญญาเช่า: one page, no SaleOrder step. The tester types a few
// basics, fills the contract, and sees each e-Contract request and response.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';
import EContractSection from './econtract/EContractSection';
import { addMonthsIso, rentalLines } from './econtract/payload';

const todayIso = () => new Date().toISOString().slice(0, 10);

const UatWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [soData, setSoData] = useState(null);
  const [basics, setBasics] = useState({ contact_name: '', contact_phone: '', contact_email: '', contract_start_date: todayIso(), contract_duration: '12' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API_URL}/rental/${id}`);
        const o = resp.data?.order;
        if (!resp.data?.success || !o || o.is_test !== 1) { navigate('/uat/rental', { replace: true }); return; }
        if (cancelled) return;
        setOrder(o);
        setSoData(o.soData);
        setBasics(prev => ({
          contact_name: o.contact_name || prev.contact_name,
          contact_phone: o.contact_phone || prev.contact_phone,
          contact_email: o.contact_email || prev.contact_email,
          contract_start_date: o.contract_start_date || prev.contract_start_date,
          contract_duration: String(o.contract_duration || prev.contract_duration)
        }));
      } catch (err) {
        toast.error('โหลดออเดอร์ทดสอบไม่สำเร็จ');
        navigate('/uat/rental', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  if (loading || !order || !soData) return <PageLoader message="กำลังโหลดพื้นที่ทดสอบ..." />;

  const so = soData.data || {};
  const addr = so.customerAddress || {};
  const duration = parseInt(basics.contract_duration, 10) || 12;

  // Same shape the real form hands to the contract section; the address comes from
  // the typed Sale Order, so there is no step 1 to fill.
  const formData = {
    customer_name: so.customerName || '',
    contract_name: so.salesOrderSubject || '',
    contact_name: basics.contact_name,
    contact_phone: basics.contact_phone,
    contact_email: basics.contact_email,
    contract_start_date: basics.contract_start_date,
    contract_duration: duration,
    contract_end_date: addMonthsIso(basics.contract_start_date, duration) || '',
    location_address_line1: addr.address1 || '',
    location_address_line2: addr.address2 || '',
    location_subdistrict: addr.subdistrict || '',
    location_district: addr.district || '',
    location_province: addr.province || '',
    location_postal_code: addr.postcode || ''
  };

  const set = (name, value) => setBasics(prev => ({ ...prev, [name]: value }));

  const validate = () => {
    if (!basics.contact_name.trim() || !basics.contact_phone.trim() || !basics.contract_start_date) {
      toast.error('กรุณากรอกข้อมูลพื้นฐานให้ครบ (ผู้ลงนามฝั่งลูกค้า เบอร์โทร และวันเริ่มสัญญา)');
      return false;
    }
    return true;
  };

  return (
    <div className="rs uat">
      <div className="uat-banner" role="note">
        <i className="bi bi-flask"></i>
        <div>
          <b>โหมดทดสอบ API</b> · ข้อมูลลูกค้าและเครื่องเป็นข้อมูลสมมติ แต่สัญญาถูกส่งไปที่ระบบ e-Contract (UAT) จริง
          ดู request และ response ของทุกการเรียกได้ที่ท้ายหน้า · <Link to="/uat/rental">กลับไปหน้าทดสอบ</Link>
        </div>
      </div>

      <header className="rs-header">
        <div>
          <h1 className="rs-title">ทดสอบสัญญา {order.venio_so_number}</h1>
          <p className="rs-subtitle">{so.customerName} · {rentalLines(soData).map(d => d.productName).join(', ')}</p>
        </div>
      </header>

      <section className="rs-card" style={{ marginBottom: 16 }}>
        <div className="rs-card__head"><div className="rs-card__headtext">
          <div className="rs-card__kicker">ข้อมูลพื้นฐาน</div><h2 className="rs-card__title">ผู้ลงนามฝั่งลูกค้าและระยะเวลา</h2>
          <p className="rs-card__desc">ข้อมูลที่ใช้ประกอบสัญญา กรอกตามต้องการ</p>
        </div></div>
        <div className="rs-grid">
          <div className="rs-field"><label htmlFor="b1">ผู้ลงนามฝั่งลูกค้า <span className="rs-req">*</span></label>
            <input id="b1" className="rs-input" value={basics.contact_name} onChange={(e) => set('contact_name', e.target.value)} placeholder="ชื่อ-นามสกุล" /></div>
          <div className="rs-field"><label htmlFor="b2">เบอร์โทร <span className="rs-req">*</span></label>
            <input id="b2" className="rs-input" inputMode="numeric" maxLength={10} value={basics.contact_phone} onChange={(e) => set('contact_phone', e.target.value.replace(/\D/g, ''))} placeholder="0XXXXXXXXX" /></div>
          <div className="rs-field"><label htmlFor="b3">อีเมลผู้ลงนามฝั่งลูกค้า</label>
            <input id="b3" className="rs-input" value={basics.contact_email} onChange={(e) => set('contact_email', e.target.value)} placeholder="ถ้ามี" /></div>
          <div className="rs-field"><label htmlFor="b4">วันเริ่มสัญญา <span className="rs-req">*</span></label>
            <input id="b4" type="date" className="rs-input" value={basics.contract_start_date} onChange={(e) => set('contract_start_date', e.target.value)} /></div>
          <div className="rs-field"><label htmlFor="b5">ระยะเวลา (เดือน)</label>
            <input id="b5" className="rs-input" inputMode="numeric" value={basics.contract_duration} onChange={(e) => set('contract_duration', e.target.value.replace(/\D/g, ''))} />
            <span className="rs-help">สิ้นสุด {formData.contract_end_date || '—'}</span></div>
        </div>
      </section>

      <EContractSection
        orderId={id}
        order={order}
        soData={soData}
        formData={formData}
        validateStep1={validate}
        saveStep1={() => axios.post(`${API_URL}/rental/${id}/save`, { ...formData })}
        onBack={() => navigate('/uat/rental')}
        backLabel="กลับไปหน้าทดสอบ"
        devView
        stayOnInvalid
      />
    </div>
  );
};

export default UatWorkspace;
